import { CaseDetail } from "../../models/CaseDetail/caseDetail.model";
import { CreateReducer } from "../../shared/utils/createReducer";
import {
  BumpCaseChatUnreadAction,
  ClearCaseChatUnreadAction,
  MergeCaseChatUnreadAction,
  SetCaseChatDrawerCaseIdAction,
  SetCaseDeletedAction,
  SetCaseDetailsUpdatedAction,
  SetNewCaseReceivedAction,
  SetPendingAction,
  SetPendingCaseIdsAction,
  SetUploadCompleteAction,
  SetUploadInProgressAction,
  SetUploadStartAction,
} from "../actions/caseActions";
import {
  BUMP_CASE_CHAT_UNREAD,
  CLEAR_CASE_CHAT_UNREAD,
  MERGE_CASE_CHAT_UNREAD,
  SET_CASE_CHAT_DRAWER_CASE_ID,
  SET_CASE_DELETED,
  SET_CASE_DETAILS_UPDATED,
  SET_NEW_CASE_CREATED,
  SET_PENDING,
  SET_PENDING_CASE_IDS,
  SET_UPLOAD_COMPLETE,
  SET_UPLOAD_IN_PROGRESS,
  SET_UPLOAD_START,
} from "../definitions/caseConstants";

export interface CaseState {
  pendingCaseIds: number[];
  completedCaseIds: number[];
  uploadStartedCaseIds: number[];
  createdCaseId?: number | null;
  updatedCaseId?: number | null;
  deletedCaseId?: number | null;
  /** Per case listing row — unread chat messages for the current user (merged from API + socket). */
  caseChatUnreadCounts: Record<number, number>;
  /** Case detail id whose chat drawer is open — used to suppress unread badge and socket bumps. */
  caseChatDrawerCaseId: number | null;
}

export interface CaseReducerProps extends CaseState {
  setPendingCaseIds: (pendingCaseIds: number[]) => SetPendingCaseIdsAction;
  setPending: (caseDetailId: number) => SetPendingAction;
  setUploadStart: (caseDetailId: number) => SetUploadStartAction;
  setUploadInProgress: (caseDetailId: number) => SetUploadInProgressAction;
  setUploadComplete: (caseDetailId: number) => SetUploadCompleteAction;
  setNewCaseCreated: (caseDetailId: number | null) => SetNewCaseReceivedAction;
  setCaseDetailUpdated: (caseDetailId: number | null) => SetCaseDetailsUpdatedAction;
  setCaseDeleted: (caseDetailId: number | null) => SetCaseDeletedAction;
  mergeCaseChatUnread: (counts: Record<number, number>) => MergeCaseChatUnreadAction;
  bumpCaseChatUnread: (caseDetailId: number) => BumpCaseChatUnreadAction;
  clearCaseChatUnread: (caseDetailId: number) => ClearCaseChatUnreadAction;
  setCaseChatDrawerCaseId: (caseDetailId: number | null) => SetCaseChatDrawerCaseIdAction;
}

const initState: CaseState = {
  pendingCaseIds: [],
  uploadStartedCaseIds: [],
  completedCaseIds: [],
  createdCaseId: null,
  updatedCaseId: null,
  deletedCaseId: null,
  caseChatUnreadCounts: {},
  caseChatDrawerCaseId: null,
};

const caseReducer = CreateReducer(initState, {
  [SET_PENDING_CASE_IDS](
    state: CaseState,
    action: SetPendingCaseIdsAction
  ): CaseState {
    const { pendingCaseIds } = action?.payload;
    return {
      ...state,
      pendingCaseIds,
    };
  },
  [SET_NEW_CASE_CREATED](
    state: CaseState,
    action: SetNewCaseReceivedAction
  ): CaseState {
    const { createdCaseId } = action?.payload;
    return {
      ...state,
      createdCaseId,
    };
  },
  [SET_CASE_DETAILS_UPDATED](
    state: CaseState,
    action: SetCaseDetailsUpdatedAction
  ): CaseState {
    const { updatedCaseId } = action?.payload;
    return {
      ...state,
      updatedCaseId,
    };
  },
  [SET_CASE_DELETED](
    state: CaseState,
    action: SetCaseDeletedAction
  ): CaseState {
    const { deletedCaseId } = action?.payload;
    return {
      ...state,
      deletedCaseId,
    };
  },
  [SET_PENDING](state: CaseState, action: SetPendingAction): CaseState {
    const { caseDetailId } = action?.payload;
    const { pendingCaseIds } = state;
    const caseIds = [...pendingCaseIds];
    if (!pendingCaseIds.includes(caseDetailId)) {
      caseIds.push(caseDetailId);
    }
    return {
      ...state,
      pendingCaseIds: caseIds,
    };
  },
  [SET_UPLOAD_START](
    state: CaseState,
    action: SetUploadStartAction
  ): CaseState {
    const { caseDetailId } = action?.payload;
    const { pendingCaseIds, uploadStartedCaseIds } = state;
    const caseIds = [...uploadStartedCaseIds];
    const pendingIds = [...pendingCaseIds];
    const caseIndex = pendingIds.findIndex((caseId) => caseId === caseDetailId);
    if (caseIndex >= 0) {
      pendingIds.splice(caseIndex, 1);
    }
    if (!uploadStartedCaseIds.includes(caseDetailId)) {
      caseIds.push(caseDetailId);
    }
    return {
      ...state,
      uploadStartedCaseIds: caseIds,
      pendingCaseIds: pendingIds,
    };
  },
  [SET_UPLOAD_IN_PROGRESS](
    state: CaseState,
    action: SetUploadStartAction
  ): CaseState {
    const { caseDetailId } = action?.payload;
    const { uploadStartedCaseIds } = state;
    const caseIds = [...uploadStartedCaseIds];
    if (!uploadStartedCaseIds.includes(caseDetailId)) {
      caseIds.push(caseDetailId);
    }
    return {
      ...state,
      uploadStartedCaseIds: caseIds,
    };
  },
  [SET_UPLOAD_COMPLETE](
    state: CaseState,
    action: SetUploadStartAction
  ): CaseState {
    const { caseDetailId } = action?.payload;
    const { uploadStartedCaseIds, completedCaseIds } = state;
    const caseIds = [...uploadStartedCaseIds];
    const completedIds = [...completedCaseIds];
    const caseIndex = caseIds.findIndex((caseId) => caseId === caseDetailId);
    if (caseIndex >= 0) {
      completedIds.push(caseDetailId);
      caseIds.splice(caseIndex, 1);
    }
    return {
      ...state,
      uploadStartedCaseIds: caseIds,
      completedCaseIds: completedIds,
    };
  },
  [MERGE_CASE_CHAT_UNREAD](
    state: CaseState,
    action: MergeCaseChatUnreadAction
  ): CaseState {
    const { counts } = action.payload;
    return {
      ...state,
      caseChatUnreadCounts: { ...state.caseChatUnreadCounts, ...counts },
    };
  },
  [BUMP_CASE_CHAT_UNREAD](
    state: CaseState,
    action: BumpCaseChatUnreadAction
  ): CaseState {
    const { caseDetailId } = action.payload;
    const cur = state.caseChatUnreadCounts[caseDetailId] ?? 0;
    return {
      ...state,
      caseChatUnreadCounts: {
        ...state.caseChatUnreadCounts,
        [caseDetailId]: cur + 1,
      },
    };
  },
  [CLEAR_CASE_CHAT_UNREAD](
    state: CaseState,
    action: ClearCaseChatUnreadAction
  ): CaseState {
    const { caseDetailId } = action.payload;
    return {
      ...state,
      caseChatUnreadCounts: {
        ...state.caseChatUnreadCounts,
        [caseDetailId]: 0,
      },
    };
  },
  [SET_CASE_CHAT_DRAWER_CASE_ID](
    state: CaseState,
    action: SetCaseChatDrawerCaseIdAction
  ): CaseState {
    return {
      ...state,
      caseChatDrawerCaseId: action.payload.caseDetailId,
    };
  },
});

export default caseReducer;
